/* global CustomEvent */
import React, {useState} from 'react';
import {useNativeNode} from './useNativeNode';
import type {AtomRenderProps} from '@packages/systems/wfdl';
import {getInPojo, getString} from '@packages/utilities/data-utils';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {
  withMeta,
  withSystem,
  Plugins,
  TypeImplementation,
  Atom,
} from '@packages/systems/wfdl';
import {iconName, typeName, qualify} from '@packages/systems/wfdl-Qualified';
import {TText, TRecord, TTypeConstructor} from '@packages/systems/wfdl-ast';
import {ExpressionEditor} from '@packages/systems/ExpressionEditor-utils';
import {createDevlinkAtom} from '@packages/utilities/integrated-types';
import SplineElement, {
  PLACEHOLDER_URL,
  isValidSplineUrl,
} from './SplineElement';
import {
  Button,
  ClockwiseIcon,
  Note,
  NoteBody,
  View,
} from '@packages/systems/spring';
import {emotionStyled} from '@packages/utilities/styled';
import {usePreviewMode, useRuntimeEnvironment} from '@packages/systems/render';

// eslint-disable-next-line webflow/module-top-level-imports
import {withLlmRender} from '@packages/utilities/llm-wfdl-renderer/withLlmRender';

const Spline = Atom.createRender(
  withLlmRender(
    ({
      attributes,
      classes,
      generateAttributes,
      environmentContext,
      data,
      elementId,
    }: AtomRenderProps) => {
      const [container, setContainerRef] = useState(null);
      const runtimeEnvironment = useRuntimeEnvironment();
      const isSsr = runtimeEnvironment === WebflowEnvironments.SSR;
      const isExport = runtimeEnvironment === WebflowEnvironments.EXPORT;
      const isEditingIx2 = environmentContext.get('isEditingIx2ActionList');
      const previewMode = usePreviewMode();
      const animationData = getInPojo(data, ['animation']);
      const animation = {
        type: getString(getInPojo(animationData, ['type'])) ?? null,
        url: getString(getInPojo(animationData, ['url'])) ?? null,
      } as const;

      if (!isValidSplineUrl(animation.url)) {
        // @ts-expect-error - TS2540 - Cannot assign to 'url' because it is a read-only property.
        animation.url = PLACEHOLDER_URL;
      }

      const props = {
        ...generateAttributes(attributes, classes),
        'data-w-id': elementId,
        'data-animation-type': animation.type,
        'data-spline-url': animation.url,
      } as const;

      const renderEmpty = isSsr || isExport || !container;

      return (
        // @ts-expect-error - TS2322 - Type 'Dispatch<SetStateAction<null>>' is not assignable to type 'LegacyRef<HTMLDivElement> | undefined'.
        <div {...props} ref={setContainerRef}>
          {renderEmpty ? (
            <canvas />
          ) : (
            <SplineElement
              // @ts-expect-error - TS2322 - Type '{ scene: any; container: never; previewMode: any; isEditingIx2: any; }' is not assignable to type 'IntrinsicAttributes & RefAttributes<any>'.
              scene={animation.url}
              container={container}
              previewMode={previewMode}
              isEditingIx2={isEditingIx2}
            />
          )}
        </div>
      );
    },
    'canvas'
  )
);

const ReloadButton = emotionStyled(Button)({
  marginTop: '$2',
});

export const SplineSettingsType = TypeImplementation.Alias({
  type: TText,
  render: function RenderEasingsEnum({expr, dispatch}) {
    const nativeNode = useNativeNode();
    const dispatchReload = React.useCallback(() => {
      nativeNode?.dispatchEvent(new CustomEvent('RELOAD_SPLINE'));
    }, [nativeNode]);
    return (
      <div>
        <ExpressionEditor expression={expr} dispatch={dispatch} />
        <ReloadButton
          data-automation-id="ReloadSplineScene"
          isFullWidth
          onClick={dispatchReload}
          isDisabled={nativeNode == null}
          prefix={<ClockwiseIcon size={12} />}
        >
          Reload Spline Scene
        </ReloadButton>
        {nativeNode == null && (
          <View marginTop="$2">
            <Note>
              <NoteBody>
                Show this element on the canvas to reload its scene.
              </NoteBody>
            </Note>
          </View>
        )}
      </div>
    );
  },
});

const config = createDevlinkAtom({
  displayName: 'Spline Scene',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Animation'), iconName('Spline')),
  },
  showEmpty: false,
  render: Spline,
  fields: {
    tag: withSystem({private: true}, TText),
    animation: TRecord({
      type: withSystem({private: true}, TText),
      url: withMeta(
        {
          placeholder: 'https://host/scene.splinecode',
        },
        withSystem(
          {
            label: 'URL',
            isLocalizable: true,
          },
          TTypeConstructor(
            qualify(Plugins.name('Animation'), typeName('SplineSettings'))
          )
        )
      ),
    }),
  },
});

export default config;
